
import { createContext, useContext, useState, useCallback, useEffect } from "react";
import IEnvironment from "../../interfaces/IEnvironment";
import EnvironmentService from "../../services/EnvironmentService";
import NotFound from "../../components/NotFound";
import DocumentHelper from "../../helpers/DocumentHelper";
import Loading from "../../components/Loading";
import FormHelper from "../../helpers/FormHelper";
import { GovernanceStatusData, GovernanceStatusEnum, OccupancyStatusData, OccupancyStatusEnum } from "../../data/Enums";
import DefaultConstants from "../../data/Constants";


interface IEnvironmentContext {
    environment: IEnvironment;
    handleChangeEnvironment: (data: IEnvironment) => void;
}


const EnvironmentContext = createContext<IEnvironmentContext | undefined>(undefined);

const EnvironmentContextProvider = (props: React.PropsWithChildren) => {
    const [loadingEnvironment, setLoadingEnvironment] = useState<boolean>(true);
    const [environment, setEnvironment] = useState<IEnvironment | undefined>(undefined);


    useEffect(() => {
        handleRefreshEnvironment();
        // eslint-disable-next-line
    }, []);


    const handleRefreshEnvironment = useCallback(() => {
        const formData = FormHelper.Create(undefined);
        
        EnvironmentService.Get(formData).then(resp => {
            const environment = resp.data.environment as IEnvironment;
            
            if (environment.partnerPms.id === undefined) {
                throw new Error(`Unable to load selected partner PMS: "${environment.partnerPms.description}"`);
            }

            environment.partnerPms.systemName = environment.partnerPms.systemName ?? DefaultConstants.systemName;
            setEnvironment(environment);


            // Configuring status with partner IDs
            OccupancyStatusData[OccupancyStatusEnum.Available].partnerId = environment.partnerPms.occupancyAvailableId;
            OccupancyStatusData[OccupancyStatusEnum.Occupied].partnerId = environment.partnerPms.occupancyOccupiedId;

            GovernanceStatusData[GovernanceStatusEnum.Clean].partnerId = environment.partnerPms.governanceCleanId;
            GovernanceStatusData[GovernanceStatusEnum.Dirty].partnerId = environment.partnerPms.governanceDirtyId;
            GovernanceStatusData[GovernanceStatusEnum.Inspection].partnerId = environment.partnerPms.governanceInspectionId;
            GovernanceStatusData[GovernanceStatusEnum.InTidying].partnerId = environment.partnerPms.governanceInTidyingId;
            GovernanceStatusData[GovernanceStatusEnum.Maintenance].partnerId = environment.partnerPms.governanceMaintenanceId;
            GovernanceStatusData[GovernanceStatusEnum.Interdicted].partnerId = environment.partnerPms.governanceInterdictedId;


            DocumentHelper.setTitle(environment.partnerPms.systemName);
            DocumentHelper.setFavIcon(environment.partnerPms.images.favIcon);
            DocumentHelper.setManifest(environment.partnerPms.description);
        }).finally(() => {
            setLoadingEnvironment(false);
        });
        // eslint-disable-next-line
    }, []);


    const handleChangeEnvironment = useCallback((data: IEnvironment) => {
        setEnvironment({
            ...environment,
            ...data
        });

        // eslint-disable-next-line
    }, [environment]);


    if (loadingEnvironment) {
        return (
            <Loading open={true} className='loading-environment' />
        );
    } else if (environment === undefined) {
        return (
            <NotFound description="_environmentNotFoundDescription" />
        );
    } else {
        const data: IEnvironmentContext = {
            environment: environment,
            handleChangeEnvironment
        };
    
        return (
            <EnvironmentContext.Provider value={data}>
                {props.children}
            </EnvironmentContext.Provider>
        );
    }
}


const useEnvironmentContext = () => {
    const context = useContext(EnvironmentContext);

    if (context === undefined) {
        throw new Error('useEnvironmentContext must be used within a EnvironmentContextProvider');
    }

    return context;
}

export {
    useEnvironmentContext
}

export default EnvironmentContextProvider;